import React, { useMemo, useState } from 'react';
import { Table } from 'syngenta-digital-cropwise-react-ui-kit';
import useTablePaginationAndSort from 'hooks/useTablePaginationAndSort';
import formatNumber from 'helpers/formatNumber';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import useTableScrollConfig from 'screens/Applications&WorkOrders/Components/DetailsTabs/hooks/useTableScrollConfig';
import { OPERATION_DETAILS_TABS_WRAPPER_SELECTOR } from 'screens/Applications&WorkOrders/Components/DetailsTabs/constants';
import Columns from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/ProductsTab/Components/Columns';
import {
  FullProduct,
  TankInformation
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import LoadingSpinner from 'components/LoadingSpinner';
import ProductViewMoreDetailsDrawer from './Components/ProductViewMoreDetailsDrawer';
import './index.css';

const PAGE_SIZE = 20;
const PRODUCTS_TABLE_SCROLL_X = 500;
const PRODUCTS_TABLE_OFFSET_WITH_TANK_INFO_EM = 44;
const PRODUCTS_TABLE_OFFSET_NO_TANK_INFO_EM = 39.75;
const DEFAULT_PRODUCTS_TABLE_LEADING_CONTENT_HEIGHT = 8;
const PRODUCTS_TABLE_LEADING_CONTENT_HEIGHT_WITH_TANK_INFO =
  60 + DEFAULT_PRODUCTS_TABLE_LEADING_CONTENT_HEIGHT;

interface ProductsTabProps {
  products: FullProduct[];
  tankInformation?: TankInformation;
  isLoading?: boolean;
}

const TankRow = ({ label, value }: { label: string; value: any }) => (
  <div
    className="flex w-full py-2 pb-7px border-b-1 border-neutral-20 gap-4"
    data-testid="property-row"
  >
    <span className="text-sm text-neutral-60 ">{label}</span>
    <span className="text-sm text-dark-grey">{value}</span>
  </div>
);

const ProductsTab = ({
  products,
  tankInformation,
  isLoading = false
}: ProductsTabProps) => {
  const [selectedProduct, setSelectedProduct] = useState<FullProduct | null>(
    null
  );
  const [viewMoreDrawerOpen, setViewMoreDrawerOpen] = useState(false);
  const {
    reactUiKitTablePaginationProp,
    reactUiKitTableOnChange,
    sortBy,
    sortDir,
    page
  } = useTablePaginationAndSort({
    pageSize: PAGE_SIZE
  }) as any;

  const productRowCountOnPage = useMemo(() => {
    if (products.length === 0) {
      return 0;
    }
    const start = (page - 1) * PAGE_SIZE;
    return Math.min(Math.max(products.length - start, 0), PAGE_SIZE);
  }, [products.length, page]);

  const hasTankInformation = useMemo(() => tankInformation !== undefined, [
    tankInformation
  ]);
  const hasPaginationNavigation = useMemo(() => products.length > PAGE_SIZE, [
    products.length
  ]);

  const handleViewMore = (product: FullProduct) => {
    setSelectedProduct(product);
    setViewMoreDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setViewMoreDrawerOpen(false);
    setSelectedProduct(null);
  };

  const columns = useMemo(
    () =>
      Columns(
        sortBy as string,
        sortDir as string,
        hasTankInformation,
        handleViewMore
      ),
    [sortBy, sortDir, hasTankInformation]
  );

  const tableScrollConfig = useTableScrollConfig({
    x: PRODUCTS_TABLE_SCROLL_X,
    rowCount: productRowCountOnPage,
    desktopOffsetEm: hasTankInformation
      ? PRODUCTS_TABLE_OFFSET_WITH_TANK_INFO_EM
      : PRODUCTS_TABLE_OFFSET_NO_TANK_INFO_EM,
    leadingHeight: hasTankInformation
      ? PRODUCTS_TABLE_LEADING_CONTENT_HEIGHT_WITH_TANK_INFO
      : DEFAULT_PRODUCTS_TABLE_LEADING_CONTENT_HEIGHT,
    wrapperSelector: OPERATION_DETAILS_TABS_WRAPPER_SELECTOR
  });

  return (
    <div data-testid="products-tab" className="px-6 py-8">
      {isLoading && (
        <div
          style={{ height: 'calc(100vh - 33em)' }}
          className="flex items-center justify-center"
        >
          <LoadingSpinner message="" className="h-full" />
        </div>
      )}
      {!isLoading && (
        <div className="flex flex-col">
          {hasTankInformation && (
            <div className="flex flex-row gap-12 mb-6">
              <div className="flex-1">
                <TankRow
                  label="Tank Size"
                  value={formatNumber(
                    tankInformation!.tankSizeValue,
                    getAbbreviatedUnit(tankInformation!.tankSizeUnit)
                  )}
                />
              </div>
              <div className="flex-1">
                <TankRow
                  label="Tank Count"
                  value={formatNumber(tankInformation!.tankCount, 'tanks')}
                />
              </div>
            </div>
          )}
          <div>
            <div
              className={`application-view-products-tab-table ${
                hasPaginationNavigation
                  ? ''
                  : 'application-view-products-tab-table-no-pagination'
              }`}
            >
              <Table
                columns={columns}
                scroll={tableScrollConfig}
                pagination={reactUiKitTablePaginationProp}
                onChange={reactUiKitTableOnChange}
                dataSource={products}
              />
            </div>
          </div>
        </div>
      )}
      <ProductViewMoreDetailsDrawer
        open={viewMoreDrawerOpen}
        onClose={handleCloseDrawer}
        product={selectedProduct}
        hasTankInformation={hasTankInformation}
      />
    </div>
  );
};

export default ProductsTab;
